import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { PageProps } from '@/lib/market/builders/shared'
import type { BreadthSeries, Leader, StatisticsData, StatWindowKey, WindowStats } from '@/lib/market/builders/statistics'
import type { ChartPane } from '@/components/charts/time-chart'

import Link from 'next/link'
import { useCallback, useMemo, useState } from 'react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Panel, PanelBody, PanelHeader } from '@/components/generals/panel'
import { buildStatistics } from '@/lib/market/builders/statistics'
import { withMarket } from '@/lib/market/builders/shared'
import { dayTicks } from '@/components/charts/scales'
import { Segmented } from '@/components/market/segmented'
import { RiskTiles } from '@/components/stock/stock-statistics'
import { formatDay } from '@/lib/market/calendar'
import { formatKes, formatNumber, formatPercent } from '@/lib/market/format'
import BarList from '@/components/charts/bar-list'
import CorrelationMatrix from '@/components/charts/correlation-matrix'
import Histogram from '@/components/charts/histogram'
import PageHeader from '@/components/generals/page-header'
import ScatterPlot from '@/components/charts/scatter-plot'
import SectorMatrix from '@/components/statistics/sector-matrix'
import TimeChart from '@/components/charts/time-chart'
import Unavailable from '@/components/market/unavailable'

type StatisticsProps = PageProps<StatisticsData>

export const getServerSideProps: GetServerSideProps<StatisticsProps> = async context => withMarket(snapshot => buildStatistics(snapshot), { req: context.req })

const WINDOW_OPTIONS: Array<{ value: StatWindowKey, label: string }> = [
    { value: '3M', label: '3 months' },
    { value: '6M', label: '6 months' },
    { value: '1Y', label: '12 months' },
    { value: '3Y', label: '3 years' }
]

const BreadthCharts = ({ breadth }: { breadth: BreadthSeries }): ReactNode => {
    const labels = useMemo(() => breadth.d.map(day => formatDay(day, 'weekday') + ' ' + formatDay(day).slice(-4)), [breadth.d])
    const xTicks = useCallback((width: number) => dayTicks(breadth.d, width), [breadth.d])

    const adPanes: ChartPane[] = useMemo(() => [{
        id: 'ad',
        height: 190,
        format: value => formatNumber(value, 0),
        series: [{ kind: 'line', id: 'ad', label: 'Advance-decline line', color: 'var(--series-1)', values: breadth.adLine }]
    }], [breadth.adLine])

    const abovePanes: ChartPane[] = useMemo(() => [{
        id: 'above',
        height: 190,
        domain: [0, 100],
        format: value => `${formatNumber(value, 0)}%`,
        guides: [{ value: 50, label: 'Half the market' }],
        series: [{ kind: 'area', id: 'above', label: 'Above 50-day average', color: 'var(--series-3)', values: breadth.above50, format: value => formatPercent(value, 1, false) }]
    }], [breadth.above50])

    const latestAbove = breadth.above50[breadth.above50.length - 1] ?? 0

    return (
        <div className='grid gap-6 lg:grid-cols-2'>
            <figure>
                <figcaption className='mb-2'>
                    <span className='block text-sm font-medium text-foreground'>Advance-decline line, 12 months</span>
                    <span className='block text-xs text-muted-foreground'>Running total of advancing minus declining shares each session.</span>
                </figcaption>
                <TimeChart count={breadth.d.length} labels={labels} xTicks={xTicks} panes={adPanes} ariaLabel='Cumulative advance decline line over 12 months' />
            </figure>

            <figure>
                <figcaption className='mb-2'>
                    <span className='block text-sm font-medium text-foreground'>Shares above their 50-day average</span>
                    <span className='block text-xs text-muted-foreground'>Currently {formatPercent(latestAbove, 0, false)} of listed shares trade above their 50-day moving average.</span>
                </figcaption>
                <TimeChart count={breadth.d.length} labels={labels} xTicks={xTicks} panes={abovePanes} ariaLabel='Share of listed companies trading above their 50-day moving average' />
            </figure>
        </div>
    )
}

const LeaderList = ({ title, description, items, format }: { title: string, description: string, items: Leader[], format: (value: number) => string }): ReactNode => {
    return (
        <div className='rounded-lg border border-border bg-background/50 p-4'>
            <h3 className='text-sm font-semibold text-foreground'>{title}</h3>
            <p className='mt-0.5 text-xs text-muted-foreground'>{description}</p>
            <ol className='mt-3 space-y-1.5'>
                {items.map((item, position) => (
                    <li key={item.id}>
                        <Link href={`/stocks/${item.slug}`} className='group flex items-center gap-3 rounded-md px-1 py-1 hover:bg-secondary/60'>
                            <span className='w-4 text-xs tabular-nums text-muted-foreground'>{position + 1}</span>
                            <span className='w-12 font-mono text-xs font-semibold text-foreground group-hover:text-primary'>{item.ticker}</span>
                            <span className='min-w-0 flex-1 truncate text-xs text-muted-foreground'>
                                <span data-mdlfy-cms={`companies:${item.id}:name`}>{item.name}</span>
                            </span>
                            <span className='text-xs font-medium tabular-nums text-foreground'>{format(item.value)}</span>
                        </Link>
                    </li>
                ))}
            </ol>
        </div>
    )
}

const WindowSection = ({ stats, sectors }: { stats: WindowStats, sectors: StatisticsData['sectors'] }): ReactNode => {
    const [sectorFocus, setSectorFocus] = useState('all')
    const rollingLabels = useMemo(() => stats.rolling.d.map(day => formatDay(day)), [stats.rolling.d])
    const rollingTicks = useCallback((width: number) => dayTicks(stats.rolling.d, width), [stats.rolling.d])
    const drawdownLabels = useMemo(() => stats.drawdown.d.map(day => formatDay(day)), [stats.drawdown.d])
    const drawdownTicks = useCallback((width: number) => dayTicks(stats.drawdown.d, width), [stats.drawdown.d])

    const rollingPanes: ChartPane[] = useMemo(() => [{
        id: 'rolling',
        height: 180,
        format: value => `${formatNumber(value, 0)}%`,
        series: [{ kind: 'line', id: 'rolling', label: 'KASI 21-session volatility', color: 'var(--series-1)', values: stats.rolling.v, format: value => formatPercent(value, 1, false) }]
    }], [stats.rolling.v])

    const drawdownPanes: ChartPane[] = useMemo(() => [{
        id: 'drawdown',
        height: 180,
        format: value => `${formatNumber(value, 0)}%`,
        series: [{ kind: 'area', id: 'drawdown', label: 'KASI below previous peak', color: 'var(--series-8)', values: stats.drawdown.v, format: value => formatPercent(value, 1) }]
    }], [stats.drawdown.v])

    const maxCap = Math.max(1, ...stats.scatter.map(point => point.cap))
    const topCaps = new Set([...stats.scatter].sort((a, b) => b.cap - a.cap).slice(0, 8).map(point => point.id))

    const scatterPoints = stats.scatter.map(point => ({
        key: point.id,
        x: point.volatility,
        y: point.annualReturn,
        label: point.ticker,
        href: `/stocks/${point.slug}`,
        highlight: sectorFocus === 'all' || point.sectorSlug === sectorFocus,
        labelled: sectorFocus === 'all' ? topCaps.has(point.id) : point.sectorSlug === sectorFocus,
        size: 3 + 7 * Math.sqrt(point.cap / maxCap),
        detail: (
            <div>
                <p className='font-mono font-semibold text-foreground'>{point.ticker}</p>
                <p className='text-muted-foreground'><span data-mdlfy-cms={`companies:${point.id}:name`}>{point.name}</span></p>
                <p className='mt-1 text-muted-foreground'>Return <span className='tabular-nums text-foreground'>{formatPercent(point.annualReturn, 1)}</span> a year</p>
                <p className='text-muted-foreground'>Volatility <span className='tabular-nums text-foreground'>{formatPercent(point.volatility, 1, false)}</span></p>
                <p className='text-muted-foreground'>Sharpe <span className='tabular-nums text-foreground'>{formatNumber(point.sharpe, 2)}</span>, value {formatKes(point.cap)}</p>
            </div>
        )
    }))

    return (
        <div className='space-y-4'>
            <Panel id='market-risk' labelledBy='market-risk-title'>
                <PanelHeader id='market-risk-title' eyebrow='Kilele All Share Index' title='Descriptive statistics of the market' description={`${stats.label}, ${stats.market.observations} daily returns.`} />
                <PanelBody className='space-y-6'>
                    <RiskTiles stats={stats.market} />

                    <div className='grid gap-6 lg:grid-cols-3'>
                        <figure>
                            <figcaption className='mb-2'>
                                <span className='block text-sm font-medium text-foreground'>Distribution of daily returns</span>
                                <span className='block text-xs text-muted-foreground'>Mean {formatPercent(stats.distribution.mean, 3)}, standard deviation {formatPercent(stats.distribution.std, 2, false)}.</span>
                            </figcaption>
                            <Histogram edges={stats.distribution.edges} counts={stats.distribution.counts} curve={stats.distribution.curve} height={200} ariaLabel='Histogram of KASI daily returns with a normal curve' />
                        </figure>

                        <figure>
                            <figcaption className='mb-2'>
                                <span className='block text-sm font-medium text-foreground'>Rolling volatility</span>
                                <span className='block text-xs text-muted-foreground'>Annualised over the previous 21 sessions.</span>
                            </figcaption>
                            <TimeChart count={stats.rolling.d.length} labels={rollingLabels} xTicks={rollingTicks} panes={rollingPanes} ariaLabel='KASI rolling volatility' />
                        </figure>

                        <figure>
                            <figcaption className='mb-2'>
                                <span className='block text-sm font-medium text-foreground'>Drawdown from peak</span>
                                <span className='block text-xs text-muted-foreground'>Deepest fall {formatPercent(stats.market.maxDrawdown, 1)} in this window.</span>
                            </figcaption>
                            <TimeChart count={stats.drawdown.d.length} labels={drawdownLabels} xTicks={drawdownTicks} panes={drawdownPanes} ariaLabel='KASI drawdown from previous peak' />
                        </figure>
                    </div>
                </PanelBody>
            </Panel>

            <div className='grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]'>
                <Panel id='risk-return' labelledBy='risk-return-title'>
                    <PanelHeader
                        id='risk-return-title'
                        eyebrow='Risk against reward'
                        title='Return and volatility of every share'
                        description='Annualised. Dot size shows market value. Select a dot to open the company.'
                        action={(
                            <label className='flex items-center gap-2 text-xs text-muted-foreground'>
                                Highlight
                                <select
                                    value={sectorFocus}
                                    onChange={event => setSectorFocus(event.target.value)}
                                    className='h-8 rounded-lg border border-border bg-background px-2 text-xs text-foreground outline-none focus-visible:border-ring'
                                >
                                    <option value='all'>All sectors</option>
                                    {sectors.map(sector => <option key={sector.slug} value={sector.slug}>{sector.name}</option>)}
                                </select>
                            </label>
                        )}
                    />
                    <PanelBody>
                        <ScatterPlot
                            points={scatterPoints}
                            height={360}
                            xLabel='Volatility, annualised'
                            yLabel='Return, annualised'
                            xFormat={value => `${formatNumber(value, 0)}%`}
                            yFormat={value => `${formatNumber(value, 0)}%`}
                            ariaLabel='Scatter of annualised return against volatility for every listed share'
                            dimOthers={sectorFocus !== 'all'}
                        />
                    </PanelBody>
                </Panel>

                <Panel id='correlation' labelledBy='correlation-title'>
                    <PanelHeader id='correlation-title' eyebrow='Co-movement' title='Correlation of the twelve largest shares' description='Pearson correlation of daily log returns. 1 moves together, 0 unrelated, negative moves apart.' />
                    <PanelBody>
                        <CorrelationMatrix labels={stats.correlation.tickers} slugs={stats.correlation.slugs} matrix={stats.correlation.matrix} />
                    </PanelBody>
                </Panel>
            </div>

            <Panel id='leaders' labelledBy='leaders-title'>
                <PanelHeader id='leaders-title' eyebrow='Leaderboards' title='Who stands out' description={stats.label} />
                <PanelBody className='grid gap-3 md:grid-cols-2 xl:grid-cols-4'>
                    <LeaderList title='Highest beta' description='Most sensitive to market moves.' items={stats.leaders.beta} format={value => formatNumber(value, 2)} />
                    <LeaderList title='Lowest volatility' description='Steadiest daily returns.' items={stats.leaders.lowVol} format={value => formatPercent(value, 1, false)} />
                    <LeaderList title='Best Sharpe ratio' description='Most return per unit of risk.' items={stats.leaders.sharpe} format={value => formatNumber(value, 2)} />
                    <LeaderList title='Deepest drawdown' description='Largest fall from a peak.' items={stats.leaders.drawdown} format={value => formatPercent(value, 1)} />
                </PanelBody>
            </Panel>

            <Panel id='seasonality' labelledBy='seasonality-title'>
                <PanelHeader id='seasonality-title' eyebrow='Calendar effects' title='Average KASI return by weekday and month' description='Mean daily return of the index for sessions falling on each weekday or in each month.' />
                <PanelBody className='grid gap-6 lg:grid-cols-2'>
                    <BarList
                        diverging
                        labelWidth='w-20'
                        items={stats.weekday.map(bucket => ({ key: bucket.label, label: bucket.label, sublabel: `${bucket.count} sessions`, value: bucket.mean, display: formatPercent(bucket.mean, 3) }))}
                    />
                    <BarList
                        diverging
                        labelWidth='w-20'
                        items={stats.month.filter(bucket => bucket.count > 0).map(bucket => ({ key: bucket.label, label: bucket.label, sublabel: `${bucket.count} sessions`, value: bucket.mean, display: formatPercent(bucket.mean, 3) }))}
                    />
                </PanelBody>
            </Panel>
        </div>
    )
}

const Statistics: FC<StatisticsProps> = ({ data, error }): ReactNode => {
    const [windowKey, setWindowKey] = useState<StatWindowKey>('1Y')
    const stats = data?.windows.find(item => item.key === windowKey) ?? data?.windows[0] ?? null

    return (
        <Layout>
            <Seo
                title='Statistics'
                description='Market breadth, sector rotation, return distributions, volatility, drawdowns, correlations and risk-adjusted leaderboards for the Kenyan equity market.'
            />

            {!data || !stats ? <Unavailable message={error} /> : (
                <>
                    <PageHeader
                        eyebrow='Statistics'
                        title='The market, measured'
                        description='Breadth and sector rotation across every horizon, then a full statistical profile of the market for the window you choose.'
                    />

                    <Panel id='sectors' labelledBy='sectors-title' className='mb-4 scroll-mt-24'>
                        <PanelHeader id='sectors-title' eyebrow='Rotation' title='Sector returns across horizons' description='Market value weighted sector returns, ordered by year to date.' />
                        <PanelBody>
                            <SectorMatrix sectors={data.sectors} />
                        </PanelBody>
                    </Panel>

                    <Panel id='breadth' labelledBy='breadth-title' className='mb-8'>
                        <PanelHeader id='breadth-title' eyebrow='Participation' title='Market breadth' description='Whether gains are broad or carried by a few large shares.' />
                        <PanelBody>
                            <BreadthCharts breadth={data.breadth} />
                        </PanelBody>
                    </Panel>

                    <div className='sticky top-14 z-30 -mx-4 mb-4 flex flex-wrap items-center gap-3 border-y border-border bg-background/90 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6'>
                        <span className='text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground'>Window</span>
                        <Segmented label='Statistics window' options={WINDOW_OPTIONS} value={windowKey} onChange={setWindowKey} size='md' />
                        <span className='text-xs text-muted-foreground'>Applies to every section below.</span>
                    </div>

                    <WindowSection key={stats.key} stats={stats} sectors={data.sectors} />
                </>
            )}
        </Layout>
    )
}

export default Statistics
